<template>
  <div class="bp-BillingInformationPage">
    <PageContentHeader
      :level="3"
      :max-width="HEADER_SIZES.FULL_NEXT"
      :title="$t('Promotions')"
      class="bl-PageContentHeader"
      no-margin
      style="--padding-top: 29px"
    />
    <div class="bp-Wrapper">
      <div class="bp-Box">
        <AppTitle :level="1" class="bs-Title" disable-margin>
          {{ $t('Get 3 months of Professional plan for $1') }}
        </AppTitle>
        <span class="bs-Subtitle">
          {{ $t('Limited time only, unlimited seats, cancel anytime.') }}
        </span>
        <AppButton
          :loading="loading"
          class="bs-Button"
          size="lg-next"
          type="success"
          @click="getCheckoutEarlyBirdCheckoutSession"
        >
          {{ $t('GET 3 MONTHS FOR $1') }}
        </AppButton>
        <span class="bs-Description">
          {{ $t('Monthly subscription') }}
          <a :href="OKR_PRICING" class="bs-Link" target="_blank">{{ $t('fee') }}</a>
          {{ $t('applies after. Offer ends 30 July 2024.') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject } from 'vue'

import BillingApiHandler from '@/api/billing'
import { getAzureProjectContext } from '@/utils/azure-devops-helpers/project-context'
import { HEADER_SIZES } from '@/utils/components-configurations/page-content-header'
import { handleError } from '@/utils/error-handling'
import { OPEN_LINK_TARGETS, openLink } from '@/utils/external-link-helper'
import { isAzureAppInjectionKey, isCrossPlatformAppInjectionKey } from '@/utils/injection-keys'

import AppButton from '@/components/ui/AppButton/AppButton'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import PageContentHeader from '@/components/ui/PageContentHeader/PageContentHeader'

const OKR_PRICING = 'https://oboard.io/okr-app-pricing'
const loading = ref(false)

const isCrossPlatformApp = inject(isCrossPlatformAppInjectionKey)
const isAzureApp = inject(isAzureAppInjectionKey)

const isCrossPlatformOrAzureApp = isCrossPlatformApp || isAzureApp

const getCheckoutEarlyBirdCheckoutSession = async () => {
  try {
    const BillingApi = new BillingApiHandler()
    loading.value = true

    let projectName = null

    if (isAzureApp) {
      const project = getAzureProjectContext()

      projectName = project?.name ?? null
    }

    const { url } = await BillingApi.earlyBirdCheckoutSession({ projectName })

    const target = isCrossPlatformOrAzureApp ? OPEN_LINK_TARGETS.BLANK : OPEN_LINK_TARGETS.SELF

    openLink({
      url,
      useBrowserWorkaround: isCrossPlatformOrAzureApp,
      target
    })
  } catch (error) {
    handleError({ error })
  } finally {
    loading.value = false
  }
}
</script>
<style lang="scss" scoped>
.bp-Wrapper {
  padding: 0 $page-right-padding 0 $page-left-padding;
}

.bp-Box {
  padding: 60px;
  background: rgba(var(--grade-high-color-rgb-next), 0.1);
  border-radius: $border-radius-lg-next;
  text-align: center;
  max-width: 1200px;
  margin: auto;
}

.bs-Title {
  font-size: $fs-48;
  line-height: 60px;
  margin-bottom: 16px;
}

.bs-Subtitle {
  margin-bottom: 48px;
  font-size: $fs-20;
  line-height: 28px;
  color: $dark-1;
  display: block;
  font-weight: fw('semi-bold');
}

.bs-Button {
  margin: auto auto 32px auto;
}

.bs-Description {
  color: $dark-2;
}
</style>
